import { useEffect, useMemo, useRef, useState } from "react";
import { AddonDataType } from "../../../interfaces/addons/Addon.type";
import useComponentsStore from "../../../store/componentsStateStore";
import useHintStore from "../../../store/hintStateStore";
import {
  getGeneralImage,
  generateStyleClasses,
  styleHelper,
} from "../../../helpers/commonFunctions";
import { logOrderProductsClickAddCart } from "../../../membership/logging/helpers/commonLoggingFunctions";

const AddonCard = ({ item }: { item: AddonDataType }) => {
  const title = item?.title;
  const description = item?.description;
  const hint = description?.hint;
  const footer = item?.footer;
  const price = footer?.price;
  const strikeThroughPrice = footer?.strikeThroughPrice;
  const actionButton = footer?.actionButton;
  const isSelected = actionButton?.isSelected;
  const defaultData = actionButton?.defaultData;
  const selectedData = actionButton?.selectedData;
  const { continueDraft } = useComponentsStore();
  const [isLoading, setIsLoading] = useState(false);
  const minusIcon = useRef(null),
    plusIcon = useRef(null);
  const { setOpen } = useHintStore();

  const hintHandler = () => {
    setOpen(true, hint?.link);
  };

  const onClickHandler = async () => {
    try {
      setIsLoading(true);
      if (!isSelected) logOrderProductsClickAddCart();
      await continueDraft(
        isSelected ? selectedData.payload : defaultData.payload
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isSelected && selectedData && selectedData.icon) {
      minusIcon.current = new Image();
      minusIcon.current.src = getGeneralImage(selectedData?.icon);
    }
    if (isSelected && defaultData && defaultData.icon) {
      plusIcon.current = new Image();
      plusIcon.current.src = getGeneralImage(defaultData?.icon);
    }
  }, [selectedData?.icon, defaultData?.icon]);

  const ButtonContent = useMemo(() => {
    return (
      <>
        <span
          style={styleHelper({
            color: isSelected
              ? selectedData?.textColor
              : defaultData?.textColor,
          })}
        >
          {isLoading && (
            <i className="vas-fs-4 vas-spinner-border vas-spinner-border-sm vas-p-2" />
          )}
          {!isLoading && isSelected && selectedData.icon && (
            <img
              className="vas"
              src={
                minusIcon?.current?.src || getGeneralImage(selectedData?.icon)
              }
              alt="minus"
              height={16}
              width={24}
            />
          )}
          {!isLoading && !isSelected && defaultData?.icon && (
            <img
              src={plusIcon?.current?.src || getGeneralImage(defaultData?.icon)}
              alt="plus"
              className="vas vas-mb-1 vas-mx-1"
              height={20}
              width={20}
            />
          )}
          {isSelected ? selectedData?.text ?? "" : defaultData?.text ?? ""}
        </span>
      </>
    );
  }, [
    defaultData?.text,
    defaultData?.textColor,
    isLoading,
    isSelected,
    selectedData?.text,
    selectedData?.textColor,
  ]);

  return (
    <div
      className={`vas-m-0 vas-p-3 vas-w-100 vas-card`}
      style={styleHelper({
        backgroundColor: item?.backgroundColor,
        border: `1px solid ${item?.borderColor}`,
      })}
    >
      <div className="vas-d-flex vas-gap-4 vas-align-item-start">
        {title.icon && (
          <img
            className="vas vas-mt-2"
            src={getGeneralImage(title.icon)}
            alt="title_icon"
            height={32}
            width={32}
          />
        )}
        <div>
          <div
            className="vas-text-28 vas-fw-bold"
            style={styleHelper({ color: title?.textColor })}
          >
            {title?.text ?? ""}
          </div>
          <span
            className={`vas-text-18 vas-mb-1 vas-text-muted vas-d-block`}
            style={styleHelper({ color: description?.descTextColor })}
          >
            {description?.descText}
          </span>
          <div>
            <span
              className={`${generateStyleClasses(
                hint?.style
              )} vas-text-18 pointerCursor`}
              style={styleHelper({ color: hint?.textColor })}
              onClick={hintHandler}
            >
              {hint?.text}
            </span>
          </div>
        </div>
      </div>
      <div
        className="vas-mb-3 vas-mt-4"
        style={styleHelper({ border: `1px solid ${item.separator?.color}` })}
      />
      <span
        className="vas-text-12 vas-fw-bold vas-text-decoration-line-through"
        style={styleHelper({ color: strikeThroughPrice?.textColor })}
      >
        {strikeThroughPrice?.text ?? ""}
      </span>
      <div className="vas-d-flex vas-justify-content-between vas-align-item-center">
        <span
          className="vas-fs-3 vas-fw-bold"
          style={styleHelper({ color: price?.textColor })}
        >
          {price?.text ?? ""}
        </span>
        <button
          disabled={isLoading}
          style={styleHelper({
            width: "170px",
            backgroundColor: isSelected
              ? selectedData?.backgroundColor
              : defaultData?.backgroundColor,
          })}
          className={`vas-btn vas-fw-bold vas-fs-4 vas-px-2 vas-border-radius`}
          onClick={onClickHandler}
        >
          {ButtonContent}
        </button>
      </div>
    </div>
  );
};

export default AddonCard;
